import * as React from 'react';
import { Row } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import { COMPANY_CREATE } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { Button } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import CreateCompanyForm from 'af-components/SharedForms/Company/BasicInfoForm';
import { CompanyFormModel } from 'af-components/SharedForms/Company/BasicInfoForm/form';
import { validate } from 'af-components/SharedForms/Company/BasicInfoForm/validations';

import * as CompanyActions from 'af-actions/companies';

interface RouteParams {
	orgAlias: string;
}

type OwnProps = CustomRouteComponentProps<RouteParams>;
type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;
type Props = FormOwnProps & InjectedFormProps<CompanyFormModel, FormOwnProps>;

const CreateCompany: React.FC<Props> = (props) => {

	const {
		invalid,
		submitting,
		handleSubmit,
		createCompany,
	} = props;

	const navigate = useNavigate();
	const location = useLocation();
	const orgAlias = React.useMemo(() => location.state.orgAlias, [location.state.orgAlias]);

	const [image, setImage] = React.useState<string | File | null>(null);
	const [shouldDeleteImage, setShouldDeleteImage] = React.useState<boolean>(false);

	const _createCompany = React.useCallback(async (form: CompanyFormModel) => {
		form.image = image;
		form.deleteImage = shouldDeleteImage;

		const rm = CompanyFormModel.toCreateRM(form);
		await createCompany(rm, orgAlias);
	}, [
		createCompany,
		image,
		orgAlias,
		shouldDeleteImage,
	]);

	const cancelCreate = React.useCallback(async () => {
		navigate(CLIENT.ORGANIZATION.SETTINGS.INFO(orgAlias));
	}, [navigate, orgAlias]);

	return (
		<div className="form-segment">
			<Breadcrumbs items={[{ label: 'Create new Company' }]} />
			<div className="form-box">
				<CreateCompanyForm
					{...props}
					formName={COMPANY_CREATE}
					image={image}
					setImage={setImage}
					setShouldDeleteImage={setShouldDeleteImage}
				/>
				<Row className="row--submit">
					<Button
						label="Cancel"
						onClick={cancelCreate}
						style="secondary"
					/>
					<Button
						disabled={invalid}
						label="Save"
						onClick={handleSubmit(_createCompany)}
						style="primary"
						submitting={submitting}
					/>
				</Row>
			</div>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		createCompany: CompanyActions.createCompany,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector,
	reduxForm<CompanyFormModel, FormOwnProps>({ form: COMPANY_CREATE, validate })
);

export default enhance(CreateCompany);
